import { defineStore } from 'pinia';
import { resolveAdapter } from '~/adapters/registry';
import { tryCatch } from '~/utils/apostatudo/try-catch';

type ModalBannerSlug =
  | 'banner-deposito'
  | 'banner-saque'
  | 'banner-indique'
  | 'login-desktop'
  | 'login-mobile'
  | 'registro-desktop'
  | 'registro-mobile';

type ModalBannerCache = {
  imageUrl: string | null;
  fetchedAt: number;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

/**
 * Mapeia o tipo do modal + device para a slug do endpoint da API.
 */
const MODAL_SLUG_MAP: Record<
  string,
  { desktop: ModalBannerSlug; mobile: ModalBannerSlug }
> = {
  deposit: { desktop: 'banner-deposito', mobile: 'banner-deposito' },
  withdraw: { desktop: 'banner-saque', mobile: 'banner-saque' },
  login: { desktop: 'login-desktop', mobile: 'login-mobile' },
  register: { desktop: 'registro-desktop', mobile: 'registro-mobile' },
  indique: { desktop: 'banner-indique', mobile: 'banner-indique' },
};

export const useModalBannersStore = defineStore('ap.modal-banners', {
  state: () => ({
    cache: {} as Record<ModalBannerSlug, ModalBannerCache>,
    loading: {} as Record<ModalBannerSlug, boolean>,
  }),

  getters: {
    getImageUrl: (state) => {
      return (modalType: string, isMobile: boolean): string | null => {
        const slugMap = MODAL_SLUG_MAP[modalType];

        if (!slugMap) return null;

        const slug = isMobile ? slugMap.mobile : slugMap.desktop;
        const cached = state.cache[slug];

        if (!cached) return null;

        return cached.imageUrl;
      };
    },

    isSlugCached: (state) => {
      return (slug: ModalBannerSlug): boolean => {
        const cached = state.cache[slug];

        if (!cached) return false;

        return Date.now() - cached.fetchedAt < CACHE_TTL_MS;
      };
    },
  },

  actions: {
    async fetchBanner(modalType: string, isMobile: boolean) {
      const slugMap = MODAL_SLUG_MAP[modalType];

      if (!slugMap) return;

      const slug = isMobile ? slugMap.mobile : slugMap.desktop;

      if (this.isSlugCached(slug)) return;
      if (this.loading[slug]) return;

      this.loading[slug] = true;

      const adapter = resolveAdapter('apiFetchPerfilOficialBanners');
      const [banners, error] = await tryCatch(adapter.handle(slug));

      if (error) {
        console.warn(`[ModalBanners] Erro ao buscar banner "${slug}":`, error);
        this.cache[slug] = { imageUrl: null, fetchedAt: Date.now() };
        this.loading[slug] = false;

        return;
      }

      const firstBanner = banners?.[0];

      this.cache[slug] = {
        imageUrl: firstBanner?.image || null,
        fetchedAt: Date.now(),
      };

      this.loading[slug] = false;
    },

    getSlugForModal(
      modalType: string,
      isMobile: boolean,
    ): ModalBannerSlug | null {
      const slugMap = MODAL_SLUG_MAP[modalType];

      if (!slugMap) return null;

      return isMobile ? slugMap.mobile : slugMap.desktop;
    },
  },
});
